import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export interface PDFOptions {
  filename?: string;
  format?: 'a4' | 'letter';
  orientation?: 'portrait' | 'landscape';
  quality?: number;
  scale?: number;
}

/** Page margins so content never sits on the edge of the paper */
const PAGE_MARGIN_MM = 14;

export const generatePDFFromHTML = async (
  elementId: string,
  options: PDFOptions = {}
): Promise<void> => {
  const {
    filename = 'document.pdf',
    format = 'a4',
    orientation = 'portrait',
    quality = 0.98,
    scale = 2
  } = options;

  try {
    const element = document.getElementById(elementId);
    if (!element) {
      throw new Error(`Element with id "${elementId}" not found`);
    }

    const canvas = await html2canvas(element, {
      scale: scale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: element.scrollWidth,
      height: element.scrollHeight
    });

    const imgWidth = canvas.width;
    const imgHeight = canvas.height;

    const pdf = new jsPDF({
      orientation: orientation,
      unit: 'mm',
      format: format
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const contentWidth = pdfWidth - PAGE_MARGIN_MM * 2;
    const contentHeight = pdfHeight - PAGE_MARGIN_MM * 2;

    const widthRatio = contentWidth / imgWidth;
    const heightRatio = contentHeight / imgHeight;
    const ratio = Math.min(widthRatio, heightRatio);

    const scaledWidth = imgWidth * ratio;
    const scaledHeight = imgHeight * ratio;

    const x = PAGE_MARGIN_MM + (contentWidth - scaledWidth) / 2;
    const y = PAGE_MARGIN_MM + (contentHeight - scaledHeight) / 2;

    const imgData = canvas.toDataURL('image/png', quality);
    pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);
    pdf.save(filename);
  } catch (error) {
    console.error('Error generating PDF from HTML:', error);
    throw error;
  }
};

const addCanvasAsPdfPages = (
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  pdfWidth: number,
  pdfHeight: number,
  quality: number,
  scale: number,
  isFirstHtmlPage: boolean
): boolean => {
  const contentPdfWidth = pdfWidth - PAGE_MARGIN_MM * 2;
  const contentPdfHeight = pdfHeight - PAGE_MARGIN_MM * 2;

  const imgWidth = canvas.width;
  const imgHeight = canvas.height;
  const widthRatio = contentPdfWidth / imgWidth;
  const pageCanvasHeight = Math.floor(contentPdfHeight / widthRatio);
  const leftoverTolerance = Math.max(scale * 3, 8);

  const placeSlice = (
    imgData: string,
    sliceHeightPx: number,
    addNewPage: boolean
  ) => {
    if (addNewPage) {
      pdf.addPage();
    }
    const scaledHeight = Math.min(
      sliceHeightPx * widthRatio,
      contentPdfHeight
    );
    pdf.addImage(
      imgData,
      'PNG',
      PAGE_MARGIN_MM,
      PAGE_MARGIN_MM,
      contentPdfWidth,
      scaledHeight
    );
  };

  // Content fits on a single PDF content area — one page only
  if (imgHeight <= pageCanvasHeight + leftoverTolerance) {
    placeSlice(
      canvas.toDataURL('image/png', quality),
      imgHeight,
      !isFirstHtmlPage
    );
    return true;
  }

  let sourceY = 0;
  let wrotePage = false;

  while (sourceY < imgHeight) {
    const remaining = imgHeight - sourceY;
    if (remaining <= leftoverTolerance) {
      break;
    }
    if (wrotePage && remaining < pageCanvasHeight * 0.1) {
      break;
    }

    const sliceHeight = Math.min(pageCanvasHeight, remaining);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = imgWidth;
    sliceCanvas.height = sliceHeight;

    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) {
      throw new Error('Unable to create canvas context for PDF page slice');
    }

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, imgWidth, sliceHeight);
    ctx.drawImage(
      canvas,
      0,
      sourceY,
      imgWidth,
      sliceHeight,
      0,
      0,
      imgWidth,
      sliceHeight
    );

    placeSlice(
      sliceCanvas.toDataURL('image/png', quality),
      sliceHeight,
      !isFirstHtmlPage || wrotePage
    );

    sourceY += sliceHeight;
    wrotePage = true;
  }

  return wrotePage || isFirstHtmlPage;
};

export const generatePDFFromHTMLString = async (
  htmlString: string,
  options: PDFOptions = {}
): Promise<void> => {
  const {
    filename: _filename = 'document.pdf',
    format = 'a4',
    orientation = 'portrait',
    quality = 0.98,
    scale = 2
  } = options;

  try {
    await new Promise((resolve) => setTimeout(resolve, 500));

    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = htmlString;
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '-9999px';
    tempContainer.style.backgroundColor = '#ffffff';
    tempContainer.style.fontFamily = 'Helvetica, Arial, sans-serif';
    tempContainer.style.fontSize = '10px';
    tempContainer.style.lineHeight = '1.2';
    tempContainer.style.color = '#333333';

    document.body.appendChild(tempContainer);

    const images = tempContainer.querySelectorAll('img');
    if (images.length > 0) {
      await Promise.all(
        Array.from(images).map(
          (img: HTMLImageElement) =>
            new Promise<void>((resolve) => {
              if (img.complete) {
                resolve();
              } else {
                img.onload = () => resolve();
                img.onerror = () => resolve();
                setTimeout(() => resolve(), 3000);
              }
            })
        )
      );
    }

    const pageElements = Array.from(
      tempContainer.querySelectorAll('.page')
    ) as HTMLElement[];

    if (pageElements.length === 0) {
      document.body.removeChild(tempContainer);
      throw new Error('No page elements found in HTML');
    }

    const pdf = new jsPDF({
      orientation: orientation,
      unit: 'mm',
      format: format
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    // Usable HTML height that maps 1:1 into one PDF page (inside margins)
    const contentPdfHeight = pdfHeight - PAGE_MARGIN_MM * 2;
    const contentPdfWidth = pdfWidth - PAGE_MARGIN_MM * 2;
    const A4_CONTENT_PX = Math.floor(595 * (contentPdfHeight / contentPdfWidth));

    let isFirstPdfOutput = true;

    for (let i = 0; i < pageElements.length; i++) {
      const pageElement = pageElements[i];

      pageElement.style.width = '595px';
      pageElement.style.height = 'auto';
      pageElement.style.minHeight = '0';
      pageElement.style.position = 'relative';
      pageElement.style.overflow = 'visible';

      await new Promise((resolve) =>
        requestAnimationFrame(() => resolve(undefined))
      );

      const naturalHeight = Math.max(
        pageElement.scrollHeight,
        pageElement.offsetHeight,
        1
      );

      if (i > 0 && naturalHeight < 120) {
        continue;
      }

      const fitsOnePage = naturalHeight <= A4_CONTENT_PX;
      const contentHeight = fitsOnePage ? A4_CONTENT_PX : naturalHeight;

      pageElement.style.minHeight = `${contentHeight}px`;
      pageElement.style.height = `${contentHeight}px`;

      const canvas = await html2canvas(pageElement, {
        scale: scale,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: 595,
        height: contentHeight,
        windowHeight: contentHeight
      });

      addCanvasAsPdfPages(
        pdf,
        canvas,
        pdfWidth,
        pdfHeight,
        quality,
        scale,
        isFirstPdfOutput
      );
      isFirstPdfOutput = false;
    }

    document.body.removeChild(tempContainer);
    pdf.save(_filename);
  } catch (error) {
    console.error('Error generating PDF from HTML string:', error);
    throw error;
  }
};
